import { useGlobal } from '@common/core-layer/composables/useGlobal'
import { useCPNavigation } from '@common/navigation-layer/composables/useNavigationContext'
import { useNuxtApp } from 'nuxt/app'

/**
 * Conversation deep-link middleware.
 *
 * A push notification for a chat resolves (via the SW cold-start URL builder and the
 * SW→app bridge's `resolvePushNotificationNavigation`) to the conversation route:
 * `communities-conversations-conversationId` — `/communities/conversations/:id`
 * (clientportal) or `/conversations/:id` (gokollab). That route only has a MOBILE
 * full-page view; on desktop chat is a floating window (`ChatContainer` + `chats-ui`
 * store), not a page.
 *
 * On MOBILE we do nothing: the conversation route renders its mobile page as intended.
 *
 * On DESKTOP we redirect BEFORE render (so the mobile conversation page never flashes)
 * to a landing that carries `?conversationId=<id>`; the client-only
 * `conversation-deep-link.client` plugin reads it and opens the floating window (window
 * state is client-side Pinia — a middleware, especially on an SSR cold load, cannot open
 * it directly). The landing is chosen to preserve context:
 *   - WARM click (user already had a page open): redirect back to the originating route
 *     (`from`) so the window opens over where they were — no jarring jump to the landing.
 *   - COLD start (fresh tab, no real `from`): redirect to the neutral `communities` landing.
 *
 * No loop: this middleware triggers on the `conversationId` route PARAM, while the
 * desktop redirect target carries `conversationId` as a QUERY on a paramless route.
 */

interface MiddlewareRoute {
  path: string
  params: Record<string, string | string[]>
  query?: Record<string, unknown>
  hash?: string
}

export interface ConversationDeepLinkNuxtApp {
  /**
   * Nuxt `runWithContext`. On the SERVER it returns a Promise (Nuxt wraps in
   * `executeAsync`); on the CLIENT it returns synchronously. Tests may pass a plain
   * `(fn) => fn()` stub. `await` handles both.
   */
  runWithContext: <T>(fn: () => T) => T | Promise<Awaited<T>>
}

export interface ConversationDeepLinkOptions {
  nuxtApp?: ConversationDeepLinkNuxtApp
}

const REDIRECT_OPTIONS = { replace: true, redirectCode: 302 as const }

export async function conversationDeepLinkMiddleware(
  to: { path: string; params: Record<string, string>; query?: Record<string, unknown> },
  from: { path: string; params: Record<string, string>; query?: Record<string, unknown> },
  options?: ConversationDeepLinkOptions
) {
  const rawId = to.params?.conversationId
  const conversationId = typeof rawId === 'string' ? rawId.trim() : ''
  // Only the conversation route carries a `conversationId` route param.
  if (!conversationId) return

  // Capture nuxtApp + composables before any await so context survives async gaps.
  // useCPNavigation → useGlobal → useRuntimeConfig must run inside runWithContext
  // (SSR async context is not always active for nested composables otherwise).
  const nuxtApp = (options?.nuxtApp ?? useNuxtApp()) as ConversationDeepLinkNuxtApp

  const { CPNavigateTo, isMobileOrTablet } = await nuxtApp.runWithContext(() => {
    const { CPNavigateTo: navigate } = useCPNavigation()
    const global = useGlobal()
    return { CPNavigateTo: navigate, isMobileOrTablet: global.isMobileOrTablet }
  })

  let isMobile = false
  await nuxtApp.runWithContext(() => {
    isMobile = isMobileOrTablet()
  })

  // Mobile: render the full-page conversation route as intended.
  if (isMobile) return

  // Desktop: open the floating window in place. Redirect BACK to the originating route
  // (warm click — the page the user was on) carrying `?conversationId=`, so the window
  // opens over their current context instead of yanking them to the landing. Only fall
  // back to the neutral `communities` landing on a cold start, where there is no real
  // originating route (`from` is the START location `/`, or equals the conversation route).
  // The client plugin reads the query, opens the window, and strips it.
  const fromPath = typeof from?.path === 'string' ? from.path : ''
  const isWarmReturn = fromPath && fromPath !== '/' && fromPath !== to.path
  if (isWarmReturn) {
    const query = { ...(from.query ?? {}), conversationId }
    return nuxtApp.runWithContext(() => navigateTo({ path: fromPath, query, ...REDIRECT_OPTIONS }))
  }

  return nuxtApp.runWithContext(() => CPNavigateTo({ name: 'communities', query: { conversationId }, options: REDIRECT_OPTIONS }))
}

export default defineNuxtRouteMiddleware(
  (to: MiddlewareRoute, from: MiddlewareRoute) =>
    conversationDeepLinkMiddleware(
      { path: to.path, params: to.params as Record<string, string>, query: to.query },
      { path: from.path, params: from.params as Record<string, string>, query: from.query },
      undefined
    ) as Promise<void>
)
